/**
 * 组织管理 API 服务
 */

import api from './api';

// 类型定义
export interface Organization {
    id: number;
    name: string;
    slug: string;
    owner: number;
    owner_name: string;
    logo?: string;
    description: string;
    plan: 'free' | 'basic' | 'pro' | 'enterprise';
    max_members: number;
    member_count: number;
    department_count: number;
    created_at: string;
    updated_at: string;
    is_active: boolean;
    invitation_code?: string;
}

export interface Department {
    id: number;
    name: string;
    parent: number | null;
    parent_name: string | null;
    description: string;
    permissions: string[];
    member_count: number;
    created_at: string;
}

export interface Member {
    id: number;
    user: number;
    user_id: number;  // 用户ID (用于supervisor关联)
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    organization: number;
    department: number | null;
    department_name: string | null;
    role: 'owner' | 'admin' | 'member';
    role_display: string;
    phone: string;
    avatar?: string;
    job_title?: string;  // 职位
    supervisor?: number | null;  // 直属上级的user_id
    supervisor_name?: string;  // 直属上级的用户名
    // HR字段
    birth_date?: string;
    gender?: string;
    hometown?: string;
    id_number?: string;
    education?: string;
    major?: string;
    emergency_contact?: string;
    emergency_phone?: string;
    hire_date?: string;
    contract_end_date?: string;
    employee_status?: 'active' | 'probation' | 'resigned';
    social_security_base?: number;
    housing_fund_base?: number;
    bank_account?: string;
    bank_name?: string;
    resume_file?: string;
    created_at: string;
    updated_at: string;
}

export interface ModuleOption {
    key: string;
    name: string;
}

// 组织 API
export const organizationAPI = {
    // 获取当前用户的组织
    getCurrent: () => api.get<Organization>('/organizations/current/'),

    // 获取组织详情
    get: (id: number) => api.get<Organization>(`/organizations/${id}/`),

    // 更新组织
    update: (id: number, data: Partial<Organization>) =>
        api.patch<Organization>(`/organizations/${id}/`, data),

    // 获取组织成员
    getMembers: (orgId: number) => api.get<Member[]>(`/organizations/${orgId}/members/`),

    // 获取可用模块列表
    getModules: () => api.get<ModuleOption[]>('/organizations/modules/'),
};

// 部门 API
export const departmentAPI = {
    // 获取部门列表
    list: () => api.get<Department[]>('/departments/'),

    // 获取部门详情
    get: (id: number) => api.get<Department>(`/departments/${id}/`),

    // 创建部门
    create: (data: Partial<Department>) => api.post<Department>('/departments/', data),

    // 更新部门
    update: (id: number, data: Partial<Department>) =>
        api.patch<Department>(`/departments/${id}/`, data),

    // 删除部门
    delete: (id: number) => api.delete(`/departments/${id}/`),

    // 获取部门成员
    getMembers: (deptId: number) => api.get<Member[]>(`/departments/${deptId}/members/`),
};

// 成员 API
export const memberAPI = {
    // 获取成员列表
    list: () => api.get<Member[]>('/members/'),

    // 获取成员详情
    get: (id: number) => api.get<Member>(`/members/${id}/`),

    // 更新成员信息
    update: (id: number, data: Partial<Member>) =>
        api.patch<Member>(`/members/${id}/`, data),

    // 创建成员
    create: (data: any) => api.post<Member>('/members/', data),

    // 修改成员角色
    changeRole: (id: number, role: 'admin' | 'member') =>
        api.post(`/members/${id}/change_role/`, { role }),

    // 修改成员部门
    changeDepartment: (id: number, departmentId: number | null) =>
        api.post(`/members/${id}/change_department/`, { department_id: departmentId }),
};
